import type { ReactNode } from 'react';
import { IconoNavegacion } from './IconoNavegacion';

// Piezas visuales compartidas. Nada de logica: solo como se ve.
//
// Todo esta pensado para un pulgar en la cancha, de noche: botones grandes,
// contraste alto, nada de menus desplegables chiquitos.

export function Boton({
  children,
  onClick,
  tipo = 'principal',
  deshabilitado,
  ancho,
  chico,
  mini,
  type = 'button',
}: {
  children: ReactNode;
  onClick?: () => void;
  tipo?: 'principal' | 'secundario' | 'peligro';
  deshabilitado?: boolean;
  ancho?: boolean;
  /** Para filas con varias acciones, donde el tamano normal le come el lugar al texto. */
  chico?: boolean;
  /** Para meter una accion en el renglon de arriba de una tarjeta, sin sumarle alto. */
  mini?: boolean;
  type?: 'button' | 'submit';
}) {
  const estilos = {
    principal: 'bg-(--color-acento) text-(--color-sobre-acento) font-semibold',
    secundario: 'bg-(--color-panel) text-(--color-texto) border border-(--color-borde)',
    peligro: 'bg-(--color-panel) text-(--color-error) border border-(--color-error)',
  }[tipo];

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={deshabilitado}
      className={`rounded-xl transition active:scale-[0.98]
        disabled:cursor-not-allowed disabled:opacity-40 ${estilos} ${ancho ? 'w-full' : ''}
        ${mini ? 'min-h-8 px-3 text-xs' : chico ? 'min-h-10 px-3 text-sm' : 'min-h-12 px-5 text-base'}`}
    >
      {children}
    </button>
  );
}

export function Tarjeta({
  children,
  className = '',
  compacta,
  sinRelleno,
}: {
  children: ReactNode;
  className?: string;
  /** Menos relleno: para listas de partidos, donde entran varias en pantalla. */
  compacta?: boolean;
  /** Sin relleno y recortada: para tablas, cuya cabecera va de borde a borde.
   *  No alcanza con pasar "p-0" en className: convive con el p-4 de aca y
   *  gana el que Tailwind escriba ultimo en el CSS, no el que va despues. */
  sinRelleno?: boolean;
}) {
  const relleno = sinRelleno ? 'overflow-hidden' : compacta ? 'px-3 py-2.5' : 'p-4';
  return (
    <section
      className={`rounded-2xl border border-(--color-borde) bg-(--color-tarjeta)
        ${relleno} ${className}`}
    >
      {children}
    </section>
  );
}

/** Los titulos van en Bebas y en mayuscula, como en la FIP. El chico sigue en
 *  Titillium: en Bebas, a 14 px, se lee mal. */
export function Titulo({ children, chico, grande }: { children: ReactNode; chico?: boolean; grande?: boolean }) {
  return (
    <h2
      className={
        chico
          ? 'text-sm font-semibold text-(--color-apagado)'
          : `font-titulo ${grande ? 'text-[28px]' : 'text-2xl'} uppercase`
      }
    >
      {children}
    </h2>
  );
}

export function Aviso({
  children,
  tono = 'info',
  legible,
}: {
  children: ReactNode;
  tono?: 'info' | 'alerta' | 'error';
  legible?: boolean;
}) {
  const estilos = {
    info: `border-(--color-borde) ${legible ? 'text-(--color-texto)' : 'text-(--color-apagado)'}`,
    alerta: 'border-(--color-alerta) text-(--color-alerta)',
    error: 'border-(--color-error) text-(--color-error)',
  }[tono];

  return (
    <p className={`rounded-xl border px-4 py-3 ${legible ? 'text-base leading-normal' : 'text-sm'} ${estilos}`} role="status">
      {children}
    </p>
  );
}

export function BotonVolver({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="-ml-1 min-h-10 self-start px-1 text-xs font-bold uppercase text-(--color-acento)"
    >
      ‹ Volver
    </button>
  );
}

/** La coronita de "El 1" al lado del nombre (el que más campeonatos del mes tiene). */
export function CoronaEl1() {
  return (
    <span className="ml-1.5 no-underline" title="“El 1”: el que más campeonatos del mes tiene" aria-label="“El 1”">
      👑
    </span>
  );
}

export function Cargando({ que = 'Cargando' }: { que?: string }) {
  return <p className="py-8 text-center text-(--color-apagado)">{que}...</p>;
}

export function Vacio({ children }: { children: ReactNode }) {
  return <p className="py-8 text-center text-sm text-(--color-apagado)">{children}</p>;
}

/** La barra de abajo. En el celular el pulgar llega ahi, no arriba. */
export function BarraInferior({
  vista,
  irA,
  pendientes,
  esAdmin,
  miPerfil,
  onMiPerfil,
}: {
  vista: string;
  irA: (v: 'inicio' | 'cargar' | 'tabla' | 'admin') => void;
  pendientes: number;
  esAdmin: boolean;
  miPerfil: boolean;
  onMiPerfil: () => void;
}) {
  const items = [
    { id: 'inicio', texto: 'Inicio' },
    { id: 'cargar', texto: 'Cargar' },
    { id: 'tabla', texto: 'Tabla' },
    { id: 'perfil', texto: 'Mi perfil' },
    ...(esAdmin ? ([{ id: 'admin', texto: 'Grupo' }] as const) : []),
  ] as const;

  return (
    <nav
      aria-label="Navegación principal"
      // Las columnas se cuentan a mano y no con grid-cols-N: Tailwind genera
      // las clases leyendo el archivo, asi que una armada al vuelo no existiria
      // en el CSS final.
      className={`sticky bottom-0 z-30 mt-auto grid gap-1 border-t border-(--color-borde)
        bg-(--color-panel) px-2 pt-1 pb-[calc(.25rem+env(safe-area-inset-bottom))]
        ${esAdmin ? 'grid-cols-5' : 'grid-cols-4'}`}
    >
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          aria-current={(item.id === 'perfil' ? miPerfil : vista === item.id) ? 'page' : undefined}
          onClick={() => item.id === 'perfil' ? onMiPerfil() : irA(item.id)}
          className={`relative flex min-h-15 min-w-0 flex-col items-center justify-center gap-0.5
            rounded-xl border text-xs transition-colors focus-visible:outline-2
            focus-visible:outline-offset-2 focus-visible:outline-(--color-texto)
            ${(item.id === 'perfil' ? miPerfil : vista === item.id)
              ? 'border-(--color-acento) bg-(--color-acento) font-bold text-(--color-sobre-acento)'
              : 'border-(--color-borde) bg-white/5 text-(--color-texto) hover:bg-white/10 active:bg-white/15'}`}
        >
          <span className={(item.id === 'perfil' ? miPerfil : vista === item.id) ? '' : 'text-(--color-acento)'}>
            <IconoNavegacion tipo={item.id} />
          </span>
          {item.texto}
          {item.id === 'inicio' && pendientes > 0 && (
            <span
              className="absolute top-1 right-1 flex h-5 min-w-5 items-center justify-center
                rounded-full border border-(--color-panel) bg-(--color-alerta) px-1 text-[11px] font-bold text-(--color-sobre-acento)"
            >
              {pendientes}
            </span>
          )}
        </button>
      ))}
    </nav>
  );
}
